import { CameraSource } from "@capacitor/camera";
import { useMutation } from "@tanstack/react-query";
import { API } from "../../../../API";
import { getDevicePhotoForDocument } from "../../../../get_device_photo";
import useOpen from "../../../../hooks/useOpen";
import {
  AgencyId,
  CaregiverComplianceItemMobile,
  CaregiverDocumentTypeId,
} from "../../../../messages";
import { STRINGS } from "../../../../strings";
import { translate } from "../../../../translate";
import { openScanner } from "../../../MedflytScanner";

interface Params {
  api: API;
  item: CaregiverComplianceItemMobile;
  agencyId: AgencyId;
  onError: (params: { title: string; message: string }) => void;
  onRequestRefresh: () => void;
  onRequestNavigateToComplianceDocumentForm: (documentTypeId: CaregiverDocumentTypeId) => void;
}

export interface UploadDocumentParams {
  id: CaregiverDocumentTypeId;
  name: string;
  isPassport: boolean;
}

function useUploadDocument(params: Params) {
  const uploadDocument = {
    id: params.item.documentType.id,
    name: params.item.documentType.name,
    isPassport: params.item.documentType.isPassport,
  };

  const mutateUploadDocument = useMutation(
    (base64: string) => {
      return uploadDocument.isPassport
        ? params.api.uploadMedflytPassportDocument({
            base64: base64,
            categoryId: CaregiverDocumentTypeId.unwrap(uploadDocument.id),
            documentTypeId: uploadDocument.id,
            documentName: uploadDocument.name,
            categoryName: uploadDocument.name,
            expiryDate: null,
          })
        : params.api.uploadMedflytPassportAgencyDocument(params.agencyId, uploadDocument.id, {
            base64: base64,
            expiryDate: null,
          });
    },
    {
      onError: () => {
        params.onError({
          title: translate(STRINGS.ERROR),
          message: translate(STRINGS.SOMETHING_WENT_WRONG),
        });
      },
      onSuccess: () => params.onRequestRefresh(),
    }
  );

  const uploadGroupDocumentModal = useOpen();
  const uploadDocumentSelectionModal = useOpen();

  const handleClickUploadDocument = () => {
    if (params.item.documentType.isFillable) {
      params.onRequestNavigateToComplianceDocumentForm(params.item.documentType.id);
      return;
    }

    const modalToOpen =
      params.item.documentType.groupType === null
        ? uploadDocumentSelectionModal
        : uploadGroupDocumentModal;

    modalToOpen.actions.open();
  };

  const handleOpenCamera = () => {
    uploadDocumentSelectionModal.actions.dismiss();
    openScanner(
      mutateUploadDocument.mutate,
      (message) => params.onError({ title: translate(STRINGS.ERROR), message: message }),
      {}
    );
  };

  const handleOpenGallery = () => {
    uploadDocumentSelectionModal.actions.dismiss();
    getDevicePhotoForDocument({
      width: 1080,
      height: 1080,
      source: CameraSource.Photos,
    })
      .then((base64) => {
        if (base64 !== null) {
          mutateUploadDocument.mutate(base64);
        }
      })
      .catch((e) => console.error(e));
  };

  return {
    uploadDocument: uploadDocument,
    uploadDocumentSelectionModal,
    uploadGroupDocumentModal,
    isLoading: mutateUploadDocument.isLoading,
    mutateUploadDocument: mutateUploadDocument,
    onClickUploadDocument: handleClickUploadDocument,
    onOpenCamera: handleOpenCamera,
    onOpenGallery: handleOpenGallery,
  };
}

export default useUploadDocument;
